/**
 * Authentication utility functions
 *
 * @purpose Role-check helpers for the authenticated user — `hasAdminRole` (admin / tenant_admin / super_admin), `isSuperAdmin`, and `hasAiSecurityReadRole` (tenant-scoped AI-security read allowlist). Use these to gate privileged UI (suppression rules, integrations management, viewing suppressed items) and tenant-scoped polling instead of comparing role strings directly.
 */

import type { UserData } from '../contexts/AuthContext'

/**
 * Check if user has admin privileges (tenant_admin or super_admin)
 * Admin users can perform privileged operations like:
 * - Creating suppression rules
 * - Managing integrations
 * - Viewing suppressed items
 */
export const hasAdminRole = (user: UserData | null): boolean => {
  return (
    user?.groups?.some(
      group =>
        group.metadata.role === 'admin' ||
        group.metadata.role === 'tenant_admin' ||
        group.metadata.role === 'super_admin',
    ) ?? false
  )
}

/**
 * Check if user is super admin (highest privilege level)
 */
export const isSuperAdmin = (user: UserData | null): boolean => {
  return (
    user?.groups?.some(group => group.metadata.role === 'super_admin') ?? false
  )
}

/**
 * Roles allowed to read the AI-security endpoints. Mirrors
 * `aiSecurityReadRoles` in cnapp-api's `internal/ainotifications/handler.go`
 * (`user` is excluded). Keep the lists in step: a role present here but not
 * there turns every poll into a 403.
 */
const AI_SECURITY_READ_ROLES = [
  'ciso',
  'security_engineer',
  'admin',
  'super_admin',
] as const

/**
 * Whether the user may read the AI-security endpoints for `tenantId`.
 *
 * Mirrors the server gate (`requireReadRole`): a global `super_admin` is
 * authorized on every tenant — it reaches tenants it holds no explicit group
 * for, so a per-tenant group match would lock it out of its own tenant
 * switcher. Otherwise the group whose slug matches the selected tenant must
 * carry an allowlisted role.
 */
export const hasAiSecurityReadRole = (
  user: UserData | null | undefined,
  tenantId: string | null | undefined,
): boolean => {
  const groups = user?.groups
  if (!groups) return false

  if (isSuperAdmin(user ?? null)) return true

  if (!tenantId) return false
  return groups.some(group => {
    if (group.group_slug !== tenantId) return false
    const role = group.metadata?.role
    return (
      typeof role === 'string' &&
      AI_SECURITY_READ_ROLES.some(allowedRole => allowedRole === role)
    )
  })
}
